import { History, Info } from "lucide-react";
import { useMemo } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { PaymentFee, ProviderRailRoute } from "../../types/paymentFees.types";
import { corridorLabel } from "../../utils/feeFormat";
import { corridorHistory } from "../../utils/feeHistory";
import { FeeChangeTimeline } from "./FeeChangeTimeline";

export interface CorridorHistoryDrawerProps {
  route: ProviderRailRoute;
  organizationId: string | null;
  organizationName: string | null;
  open: boolean;
  onClose: () => void;
  fees: PaymentFee[];
  now: number;
  exampleAmountFor: (currency: string | null | undefined) => string;
  organizationNames: Record<string, string>;
  authorNames: Record<string, string>;
}

export function CorridorHistoryDrawer({
  route,
  organizationId,
  organizationName,
  open,
  onClose,
  fees,
  now,
  exampleAmountFor,
  organizationNames,
  authorNames,
}: CorridorHistoryDrawerProps) {
  const { t } = useTranslations("paymentFees");
  const labels = useFeeLabels();

  const changes = useMemo(
    () =>
      corridorHistory(route, organizationId, fees, now, (fee) =>
        exampleAmountFor(fee.currency ?? route.currency),
      ),
    [route, organizationId, fees, now, exampleAmountFor],
  );

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t("history_drawer_title", { corridor: corridorLabel(route, labels.directionShort) })}
      description={
        organizationId
          ? t("history_drawer_desc_org", { name: organizationName ?? organizationId })
          : t("history_drawer_desc_general")
      }
      size="lg"
      pinLastChild={false}
    >
      <div className="space-y-4">
        {changes.length === 0 ? (
          <ColocaEmpty icon={History} title={t("history_empty")} surface="card" />
        ) : (
          <FeeChangeTimeline
            changes={changes}
            organizationNames={organizationNames}
            authorNames={authorNames}
          />
        )}
        <ColocaAlert variant="secondary" icon={Info} text={t("history_deleted_note")} />
      </div>
    </ColocaDrawer>
  );
}
